<template>
  <div
    class="relative max-w-screen-4xl mx-auto h-screen flex flex-col lg:flex-row items-center justify-between px-6 lg:px-32"
  >
    <div class="absolute inset-0 overflow-hidden">
      <video
        v-show="currentVideo === 1"
        autoplay
        muted
        loop
        playsinline
        class="w-full h-full object-cover"
      >
        <source
          src="../assets/video/WEBSITE LAND RESIZE.mp4"
          type="video/mp4"
        />
        Your browser does not support the video tag.
      </video>
      <div class="absolute inset-0 bg-black opacity-50"></div>
    </div>

    <!-- SVG Curve (Pushed Down Properly) -->
    <svg
      viewBox="0 0 1440 320"
      class="absolute bottom-[-1px] left-0 w-full z-0"
    >
      <path
        fill="#ffffff"
        d="M0,224L80,208C160,192,320,160,480,176C640,192,800,256,960,266.7C1120,277,1280,235,1360,213.3L1440,192V320H0Z"
      ></path>
    </svg>
  </div>

  <!-- Our Strategic Pillars (Moves Up Correctly) -->
  <div class="md:px-10 max-w-screen-4xl mx-auto relative z-10 -mt-28 lg:-mt-20">
    <!-- Title Section -->
    <div class="flex items-center px-6 lg:px-2 lg:ml-40 ml-0 strategic-card">
      <button
        @click="openModal"
        class="bg-yellow text-white text-sm px-6 py-2 font-semibold hover:bg-black hover:text-white"
      >
        Download Newsletter
      </button>
    </div>

    <!-- Modal Section -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 px-4"
    >
      <div class="bg-white px-5 py-8 rounded-lg shadow-xl max-w-lg w-full">
        <!-- Close Button -->
        <div class="flex justify-end items-center mb-6">
          <button
            @click="closeModal"
            class="text-gray-500 text-3xl hover:text-yellow transition"
          >
            &times;
          </button>
        </div>

        <!-- Form -->
        <form @submit.prevent="download">
          <div class="mb-5">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Name</label
            >
            <input
              v-model="formData.name"
              type="text"
              class="w-full border rounded-md p-2 focus:ring focus:ring-yellow-300 outline-none"
              required
            />
          </div>

          <div class="mb-5">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Email</label
            >
            <input
              v-model="formData.email"
              type="email"
              class="w-full border rounded-md p-2 focus:ring focus:ring-yellow-300 outline-none"
              required
            />
          </div>

          <!-- Buttons with Better Spacing and Layout -->
          <div class="flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              @click="downloadFile('land')"
              class="flex-1 bg-yellow text-white py-2 rounded-md font-medium transition-transform transform hover:scale-110 focus:outline-none focus:ring-4 hover:bg-black hover:text-white"
            >
              📂 Download Land
            </button>
            <button
              type="button"
              @click="downloadFile('house')"
              class="flex-1 bg-yellow text-white py-2 rounded-md font-medium transition-transform transform hover:scale-110 focus:outline-none focus:ring-4 hover:bg-black hover:text-white"
            >
              🏠 Download Ultra
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Cards Section -->
    <div
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14 mb-14 px-4 lg:px-2"
    >
      <div
        v-for="(pillar, index) in strategicPillars"
        :key="index"
        class="strategic-card bg-secondary shadow-lg rounded-lg p-6 text-center cursor-pointer"
        style="will-change: transform, box-shadow"
      >
        <div class="flex justify-center items-center mb-4">
          <div
            class="bg-yellow w-12 h-12 flex items-center justify-center rounded-full"
          >
            <i :class="pillar.icon" class="text-white text-xl"></i>
          </div>
        </div>
        <h3 class="text-lg font-semibold text-black">{{ pillar.title }}</h3>
        <ul class="text-gray-900 mt-2">
          <li v-for="(desc, i) in pillar.description" :key="i" class="mb-2">
            {{ desc }}
          </li>
        </ul>
      </div>
    </div>
  </div>

  <!-- Component Sections -->
  <valueProposition />
  <ourStory />
  <!-- <mshelAdvert/> -->
  <ourProperties />
  <mshelYola />
  <testimonialSection />
  <faqSection />
</template>

<script>
import ourStory from "../components/ourStory.vue";
import mshelYola from "../components/mshelYola.vue";
import valueProposition from "../components/valueProposition.vue";
import ourProperties from '../components/ourProperties.vue';
import faqSection from '../components/faqSection.vue';
import testimonialSection from '../components/testimonialSection.vue';
import { showErrorAlert, showLoadingAlert, showSuccessAlert } from "../services/reusableService";

export default {
  components: {
    ourStory,
    mshelYola,
    valueProposition,
    ourProperties,
    faqSection,
    testimonialSection,
  },
  data() {
    return {
      isMenuOpen: false,
      isModalOpen: false,
      currentIndex: 0,
      currentVideo: 1,
      formData: {
        name: "",
        email: "",
      },
      strategicPillars: [
        {
          title: "Our Vision",
          icon: "fa-regular fa-eye",
          description: [
            "To be the most exceptional real estate company empowering your dream of owning a home and a piece of the world",
          ],
        },
        {
          title: "Our Mission",
          icon: "fa-solid fa-paper-plane",
          description: [
            "We redefine your experience of what real estate value is and empower your dreams",
          ],
        },
        {
          title: "Our Core Values",
          icon: "fa-solid fa-ring",
          description: [
            "Impeccable Customer Satisfaction",
            "Honesty & Integrity",
            "Professionalism & Expertise",
          ],
        },
      ],
    };
  },
  mounted() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in");
          } else {
            entry.target.classList.remove("fade-in");
          }
        });
      },
      {
        threshold: 0.2, // Adjust for smoother animation trigger
      }
    );

    document
      .querySelectorAll(".strategic-card")
      .forEach((card) => observer.observe(card));
  },
  methods: {
    openModal() {
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    resetForm() {
      this.formData = {
        name: "",
        email: "",
      };
    },
    async downloadFile(type) {
      if (!this.formData.name || !this.formData.email) {
        showErrorAlert("Kindly fill the form before you can download!");
        return;
      }

      const months = [
        "january",
        "february",
        "march",
        "april",
        "may",
        "june",
        "july",
        "august",
        "september",
        "october",
        "november",
        "december",
      ];
      const currentDate = new Date();
      const currentMonth = months[currentDate.getMonth()];
      const currentYear = currentDate.getFullYear();
      const fileName = `${currentMonth}-newsletter-${type}-${currentYear}.pdf`;
      const newsletterUrl = `https://mshelhomes.com/images/pdf/${fileName}`;

      try {
        showLoadingAlert("Preparing your download...");

        // Simulating a small delay for better UX
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = newsletterUrl;
          a.target = "_blank";
          a.download = fileName;
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          showSuccessAlert("Download started successfully!");
          this.resetForm();
          this.closeModal();
        }, 1000);
      } catch (error) {
        showErrorAlert("Failed to start download. Please try again!");
      }
    },
  }
};
</script>

<style scoped>
svg path {
  fill: #ffffff !important;
  stroke: none !important;
  box-shadow: none !important;
}

svg {
  overflow: hidden !important;
}

.absolute.bottom-0 {
  line-height: 0 !important;
  padding-bottom: 0 !important;
  margin-bottom: -2px;
}

.overflow-hidden {
  overflow: hidden !important;
}

.strategic-style {
  font-family: "Caveat";
}

.strategic-card {
  opacity: 0;
  transform: translateY(50px);
  transition: opacity 1s ease-out, transform 1s ease-out;
  will-change: opacity, transform;
}

.strategic-card.fade-in {
  opacity: 1;
  transform: translateY(0);
}

.strategic-card:hover {
  transform: scale(1.03);
  /* box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2); */
  transition: transform 0.4s ease-out, box-shadow 0.4s ease-out;
  /* background-color: inherit !important; */
  box-shadow: none !important;
}
</style>
